// Explainer workflow of the Video Agent: animated "boards" (bean characters,
// flat icons, arrows, headers) whose elements pop in on the narration.
// Client-safe: shared by /create-video and /api/generate-video-project.

import { assetUrl } from '@/components/video-editor/explainer/assets';
import type { ThemeId } from '@/components/video-editor/explainer/types';

/** Behind a flag until the Lambda site that renders boards is deployed. */
export function isExplainerWorkflowEnabled(): boolean {
  return process.env.NEXT_PUBLIC_EXPLAINER_WORKFLOW === 'true';
}

export const EXPLAINER_DURATION = {
  /** Paid plans, seconds. */
  min: 10,
  max: 600,
  default: 60,
  /** Free plans get a short try (narration within ~10% of it, see the
   * Video Agent's drift repair). */
  free: 30,
  /** When the user leaves the length on Auto, the writer picks within this. */
  autoMin: 45,
  autoMax: 120,
} as const;

/** Words per second the script is written to (an even narration pace). */
export const EXPLAINER_WORDS_PER_SECOND = 2.5;

/**
 * How narration length is counted in a language and how fast it's spoken:
 * words, or characters in Chinese and Japanese (written without spaces).
 * Korean, Arabic and Hindi words carry more syllables than English ones.
 */
export interface SpeechRate {
  unit: 'words' | 'characters';
  perSecond: number;
}

// Measured on catalog narrators' real audio, pauses included (Flash v2.5,
// 2026-10-07): Japanese 3.8 characters per second, Chinese 3.05, Korean
// 1.9 words, Arabic 1.5, Hindi 2.6. Narrators read slower than people talk.
const SPEECH_RATES: Record<string, SpeechRate> = {
  ja: { unit: 'characters', perSecond: 4 },
  zh: { unit: 'characters', perSecond: 3.3 },
  ko: { unit: 'words', perSecond: 2 },
  ar: { unit: 'words', perSecond: 1.6 },
  hi: { unit: 'words', perSecond: 2.5 },
};

/**
 * The speech rate of a language (ISO 639-1), else of the writing system of
 * `sample` (a script written without a language picked), else English's.
 */
export function speechRate(
  language?: string | null,
  sample?: string,
): SpeechRate {
  const base = language?.toLowerCase().split(/[-_]/)[0];
  if (base && SPEECH_RATES[base]) return SPEECH_RATES[base];
  if (!base && sample) {
    if (/[\u3040-\u30ff]/.test(sample)) return SPEECH_RATES.ja;
    if (/[\u4e00-\u9fff]/.test(sample)) return SPEECH_RATES.zh;
    if (/[\uac00-\ud7a3]/.test(sample)) return SPEECH_RATES.ko;
    if (/[\u0600-\u06ff]/.test(sample)) return SPEECH_RATES.ar;
    if (/[\u0900-\u097f]/.test(sample)) return SPEECH_RATES.hi;
  }
  return { unit: 'words', perSecond: EXPLAINER_WORDS_PER_SECOND };
}

/**
 * An Explainer is a `video` (16:9 or 9:16, up to 10 minutes) or a `short`:
 * a vertical 20-60 s clip for YouTube Shorts, TikTok and Reels, written
 * around a hook and paced faster.
 */
export type ExplainerFormat = 'video' | 'short';

export function isExplainerFormat(value: unknown): value is ExplainerFormat {
  return value === 'video' || value === 'short';
}

export const EXPLAINER_SHORT_DURATION = {
  min: 20,
  max: 60,
  default: 45,
} as const;

/** How a Short is built; `auto` lets the writer pick what fits the topic. */
export type ExplainerShortStyle =
  | 'auto'
  | 'quiz'
  | 'versus'
  | 'ranking'
  | 'explain';

export const EXPLAINER_SHORT_STYLES: readonly {
  id: ExplainerShortStyle;
  label: string;
  description: string;
}[] = [
  { id: 'auto', label: 'Auto', description: 'The writer picks what fits' },
  { id: 'quiz', label: 'Quiz', description: 'Ask, count down, reveal' },
  { id: 'versus', label: 'This vs that', description: 'Two things compared' },
  { id: 'ranking', label: 'Top list', description: 'Counted down to number 1' },
  { id: 'explain', label: 'Quick explainer', description: 'One idea, fast' },
];

export function isExplainerShortStyle(
  value: unknown,
): value is ExplainerShortStyle {
  return EXPLAINER_SHORT_STYLES.some((s) => s.id === value);
}

/** Seconds of silence after a question the viewer is asked to answer. */
export const EXPLAINER_PAUSE_SECONDS = 3;

/** "45s", "2m", "2m 30s". */
export function formatExplainerDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

/**
 * Rough minutes from Generate to a ready project, for the form. Measured
 * 2026-10-06: a 60 s video took 80 s, a 30 s one 58 s; the script writer,
 * narration and board planning all grow with the length.
 */
export function explainerGenerationMinutes(seconds: number): number {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  return Math.max(1, Math.round((60 + 0.6 * safe) / 60));
}

/**
 * The looks a user picks on the form; every board of the video is stamped
 * with it (BoardSpec.theme). Classic pops elements in, whiteboard and
 * chalkboard draw them with a hand (src/components/video-editor/explainer/
 * themes.ts), and the other four have their own renderers with a moving
 * camera and sound effects (explainer/looks/).
 */
export const EXPLAINER_LOOKS: readonly {
  id: ThemeId;
  label: string;
  description: string;
  /** Card image (R2, see explainer/assets.ts). */
  image: string;
  /** A few seconds of the look, played while the card is hovered (R2). */
  clip: string;
  /** Plays sound effects as elements appear (the form can turn them off). */
  sfx?: boolean;
}[] = [
  {
    id: 'classic',
    label: 'Classic',
    description: 'Colour icons that pop in',
    image: assetUrl('cards/look-classic.jpg'),
    clip: assetUrl('cards/look-classic.mp4'),
  },
  {
    id: 'glass',
    label: '3D Glass',
    description: 'Glass cards and 3D icons',
    image: assetUrl('cards/look-glass.jpg'),
    clip: assetUrl('cards/look-glass.mp4'),
    sfx: true,
  },
  {
    id: 'kinetic',
    label: 'Kinetic Type',
    description: 'The narration in big type',
    image: assetUrl('cards/look-kinetic.jpg'),
    clip: assetUrl('cards/look-kinetic.mp4'),
    sfx: true,
  },
  {
    id: 'paper',
    label: 'Paper Cutout',
    description: 'Stop-motion paper craft',
    image: assetUrl('cards/look-paper.jpg'),
    clip: assetUrl('cards/look-paper.mp4'),
    sfx: true,
  },
  {
    id: 'neon',
    label: 'Neon Night',
    description: 'Neon signs on a brick wall',
    image: assetUrl('cards/look-neon.jpg'),
    clip: assetUrl('cards/look-neon.mp4'),
    sfx: true,
  },
  {
    id: 'whiteboard',
    label: 'Whiteboard',
    description: 'Drawn by hand with a pen',
    image: assetUrl('cards/look-whiteboard.jpg'),
    clip: assetUrl('cards/look-whiteboard.mp4'),
  },
  {
    id: 'chalkboard',
    label: 'Chalkboard',
    description: 'Chalk on a green board',
    image: assetUrl('cards/look-chalkboard.jpg'),
    clip: assetUrl('cards/look-chalkboard.mp4'),
  },
];

/**
 * The Explainer card's silent preview loop (remotion/samples Preview-16x9,
 * 26 s, 960×540, 345 KB) and its poster, with the other board assets on R2.
 * Not media.easy-peasy.ai/assets/explainer-boards-preview.mp4: that key was
 * requested before anything was there, and the CDN caches a 404.
 */
export const EXPLAINER_PREVIEW_VIDEO = assetUrl('cards/preview.mp4');
export const EXPLAINER_PREVIEW_POSTER = assetUrl('cards/look-classic.jpg');

export const DEFAULT_EXPLAINER_LOOK: ThemeId = 'classic';

export function isExplainerLook(value: unknown): value is ThemeId {
  return (
    typeof value === 'string' && EXPLAINER_LOOKS.some((l) => l.id === value)
  );
}

/** Whether a look plays sound effects as elements appear. */
export function explainerLookHasSfx(look: ThemeId): boolean {
  return EXPLAINER_LOOKS.some((l) => l.id === look && l.sfx);
}

export const EXPLAINER_ASPECT_RATIOS = ['16:9', '9:16'] as const;
export type ExplainerAspectRatio = (typeof EXPLAINER_ASPECT_RATIOS)[number];

export function isExplainerAspectRatio(
  value: unknown,
): value is ExplainerAspectRatio {
  return value === '16:9' || value === '9:16';
}

/**
 * Narration languages the boards can draw: the looks' fonts cover Latin,
 * Cyrillic, Greek and Vietnamese, and script fonts (explainer/themes.ts)
 * Japanese, Chinese, Korean, Hindi (Devanagari) and Arabic, drawn right to
 * left. Hebrew and Thai have no fonts yet.
 */
export const EXPLAINER_LANGUAGE_CODES: readonly string[] = [
  'ar',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'fi',
  'fr',
  'hi',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'ms',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sv',
  'tl',
  'tr',
  'uk',
  'vi',
  'zh',
];

export function isExplainerLanguage(code: string | null | undefined): boolean {
  return !!code && EXPLAINER_LANGUAGE_CODES.includes(code);
}

// ── Pricing ─────────────────────────────────────────────────────────
//
// Cost recovery at 1 credit = $0.02 (Dmytro, 2026-10-06): about $2 a video
// for the script writer (with web research), the board planner and the
// thumbnail render. Narration is billed separately to the speech allowance,
// like every other Video Agent workflow.

export const EXPLAINER_BASE_CREDITS = 80;
export const EXPLAINER_CREDITS_PER_MINUTE = 10;

/** 1 min = 90, 2 min = 100, 5 min = 130, 10 min = 180 credits. */
export function explainerCredits(seconds: number): number {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const minutes = Math.max(1, Math.ceil(safe / 60));
  return EXPLAINER_BASE_CREDITS + EXPLAINER_CREDITS_PER_MINUTE * minutes;
}

/**
 * What a finished video is charged: the quote shown before generating, or
 * less when the narration came out shorter than requested. Never more.
 * `modelShare` is the share of the video's seconds whose boards the model
 * planned; stretches drawn by the fallback planner are free, so a video
 * planned entirely by the fallback costs nothing.
 */
export function explainerCharge(
  quotedSeconds: number,
  actualSeconds: number,
  modelShare = 1,
): number {
  const share = Number.isFinite(modelShare)
    ? Math.min(1, Math.max(0, modelShare))
    : 0;
  return Math.floor(
    Math.min(explainerCredits(quotedSeconds), explainerCredits(actualSeconds)) *
      share,
  );
}

/** Seconds the quote is based on: the requested length, or Auto's maximum. */
export function explainerQuoteSeconds(
  durationSeconds: number,
  autoDuration: boolean,
): number {
  return autoDuration ? EXPLAINER_DURATION.autoMax : durationSeconds;
}

// "Translate this video": no research or writing from scratch, the same
// voicing, planning and stills as a new video.
export const EXPLAINER_TRANSLATE_BASE_CREDITS = 60;

/** 1 min = 70, 5 min = 110, 10 min = 160 credits. */
export function explainerTranslateCredits(seconds: number): number {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const minutes = Math.max(1, Math.ceil(safe / 60));
  return (
    EXPLAINER_TRANSLATE_BASE_CREDITS + EXPLAINER_CREDITS_PER_MINUTE * minutes
  );
}

/**
 * Seconds a translation is quoted on: its source's length. A translation
 * that runs longer (German takes more syllables) is never charged more.
 */
export function explainerTranslateQuoteSeconds(sourceSeconds: number): number {
  return Number.isFinite(sourceSeconds) && sourceSeconds > 0
    ? Math.ceil(sourceSeconds)
    : 0;
}

/** A translation's charge, on the same rules as explainerCharge. */
export function explainerTranslateCharge(
  quotedSeconds: number,
  actualSeconds: number,
  modelShare = 1,
): number {
  const share = Number.isFinite(modelShare)
    ? Math.min(1, Math.max(0, modelShare))
    : 0;
  return Math.floor(
    Math.min(
      explainerTranslateCredits(quotedSeconds),
      explainerTranslateCredits(actualSeconds),
    ) * share,
  );
}

// ── Text limits ─────────────────────────────────────────────────────
//
// Recommended maximum characters per text slot, per board canvas. They keep
// text at full size; the renderer shrinks a longer text to fit, and the
// planner cuts anything past 1.5x the limit at a word boundary.

export type ExplainerTextSlot =
  | 'header'
  | 'speech'
  | 'name'
  | 'title'
  | 'chapter'
  | 'iconLabel'
  | 'statLabel'
  | 'listItem'
  | 'chartLabel'
  | 'chartValue'
  | 'eventLabel'
  | 'mapLabel'
  | 'source'
  | 'question'
  | 'option'
  | 'side'
  | 'versusLabel'
  | 'versusValue'
  | 'rankItem';

export const EXPLAINER_TEXT_LIMITS: Record<
  ExplainerTextSlot,
  Record<ExplainerAspectRatio, number>
> = {
  header: { '16:9': 60, '9:16': 40 },
  speech: { '16:9': 40, '9:16': 32 },
  name: { '16:9': 18, '9:16': 14 },
  title: { '16:9': 30, '9:16': 22 },
  chapter: { '16:9': 32, '9:16': 24 },
  iconLabel: { '16:9': 24, '9:16': 22 },
  statLabel: { '16:9': 30, '9:16': 34 },
  listItem: { '16:9': 40, '9:16': 34 },
  chartLabel: { '16:9': 18, '9:16': 26 },
  chartValue: { '16:9': 10, '9:16': 10 },
  eventLabel: { '16:9': 30, '9:16': 34 },
  mapLabel: { '16:9': 20, '9:16': 16 },
  source: { '16:9': 48, '9:16': 40 },
  question: { '16:9': 70, '9:16': 56 },
  option: { '16:9': 34, '9:16': 30 },
  side: { '16:9': 22, '9:16': 14 },
  versusLabel: { '16:9': 20, '9:16': 26 },
  versusValue: { '16:9': 16, '9:16': 14 },
  rankItem: { '16:9': 40, '9:16': 30 },
};

export function explainerHardTextLimit(
  slot: ExplainerTextSlot,
  aspect: ExplainerAspectRatio,
): number {
  return Math.ceil(EXPLAINER_TEXT_LIMITS[slot][aspect] * 1.5);
}

/**
 * A text's length against the text limits, in Latin letters: a Chinese,
 * Japanese or full-width character is as wide as two, a Hangul syllable
 * almost two, a combining mark (Devanagari vowel signs, Arabic harakat)
 * nothing.
 */
export function textUnits(text: string): number {
  let n = 0;
  for (const c of text) n += charUnits(c);
  return n;
}

function charUnits(c: string): number {
  const cp = c.codePointAt(0)!;
  if (/\p{M}/u.test(c)) return 0;
  if (
    (cp >= 0x2e80 && cp <= 0x9fff) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xff00 && cp <= 0xff60)
  ) {
    return 2;
  }
  if (cp >= 0xac00 && cp <= 0xd7a3) return 1.8;
  return 1;
}

/** The longest start of `text` within `max` units (textUnits). */
export function cutToUnits(text: string, max: number): string {
  let n = 0;
  let out = '';
  for (const c of text) {
    n += charUnits(c);
    if (n > max) break;
    out += c;
  }
  return out;
}
